import React, { useEffect, useState } from 'react';
import { authApiClient } from '../../services/authApi';

export type CronAPeriod = 'daily' | 'weekly';

export interface CronAPeriodRow {
  period_start: string;
  discovered: number | null;
  ingested: number;
  failed: number;
  not_parsed: number | null;
  // New products vs new versions of held products (absent from older APIs;
  // versions are null for a family that does not report them).
  products_discovered?: number | null;
  versions_discovered?: number | null;
  products_ingested?: number | null;
  versions_ingested?: number | null;
  runs: number;
  last_status: string | null;
  source: string | null;
}

export interface CronAFamily {
  family: string;
  region: string | null;
  last_run_at: string | null;
  scheduled: boolean;
  periods: CronAPeriodRow[];
}

interface CronASummaryResponse {
  families: CronAFamily[];
}

// Range choices: days for daily, weeks for weekly (sent to the API as days).
const RANGE_OPTIONS: Record<CronAPeriod, { value: number; label: string }[]> = {
  daily: [
    { value: 7, label: 'Last 7 days' },
    { value: 14, label: 'Last 14 days' },
    { value: 30, label: 'Last 30 days' },
  ],
  weekly: [
    { value: 4, label: 'Last 4 weeks' },
    { value: 8, label: 'Last 8 weeks' },
    { value: 12, label: 'Last 12 weeks' },
  ],
};
const DEFAULT_RANGE: Record<CronAPeriod, number> = { daily: 14, weekly: 8 };

export const FAILED_TOOLTIP =
  'Most documents that failed in a single pass during this period ' +
  '(failed docs are retried every pass, so passes are not added up). ' +
  'For sba: failed documents created in the period.';
export const NOT_PARSED_NA_TOOLTIP = 'No OCR cost cap on this family';
export const NOT_PARSED_TOOLTIP =
  'Documents stopped by the OCR cost gate (capped mid-parse or skipped before spend)';
// Column help, shared by Latest (scope 'run') and Daily / Weekly (scope 'period').
export type CronAScope = 'run' | 'period';
const FOUND_WHEN: Record<CronAScope, string> = {
  run: 'Count from this run.',
  period: 'Count from the last run of the period.',
};
const SAVED_WHEN: Record<CronAScope, string> = {
  run: 'Count from this run.',
  period: 'All runs of the period, added up.',
};
// Browsers show '\n' in a title tooltip as a line break.
export const cronAColumnTooltips = (scope: CronAScope) => ({
  newProducts:
    'Drugs found at the source that we do not have at all (no version of them).\n' +
    FOUND_WHEN[scope],
  newVersions:
    'Drugs we already have, where the source has a version we have not stored yet.\n' +
    `${FOUND_WHEN[scope]}\n` +
    '"—" = this source has no version numbers, so it is not counted.',
  productsIngested:
    'How many of the New products were downloaded and saved.\n' +
    `${SAVED_WHEN[scope]}\n` +
    'Each run saves only a limited number, so this can be lower than New products.',
  versionsIngested:
    'How many of the New versions were downloaded and saved.\n' +
    `${SAVED_WHEN[scope]}\n` +
    '"—" = this source has no version numbers, so it is not counted.',
});
export const VERSIONS_NA_TOOLTIP = 'This source has no version numbers, so versions are not counted';

// Readable names for the Cron A family keys (backend CRON_A_FAMILIES), region
// first. Unknown keys fall back to the raw key.
export const FAMILY_LABELS: Record<string, string> = {
  us_spl: 'US Label (SPL)',
  sba: 'US FDA Review (SBA)',
  ca_pm: 'Canada Product Monograph',
  uk_pil: 'UK Patient Leaflet (PIL)',
  epar_epar_public_assessment: 'EU EPAR Assessment Report',
  epar_smop: 'EU EPAR Summary of Opinion',
  eu_smpc: 'EU SmPC',
  jp_xml: 'Japan Label (XML)',
  jp_pdf_patient_leaflet: 'Japan Patient Leaflet',
  jp_pdf_interview_form: 'Japan Interview Form',
  jp_pdf_rmp: 'Japan Risk Mgmt Plan',
  au_pi: 'Australia Product Information (PI)',
  au_cmi: 'Australia Consumer Info (CMI)',
  zh_pdf: 'China Label (PDF)',
  ch_pdf_smpc: 'Switzerland SmPC (Fachinformation)',
  ch_pdf_pil: 'Switzerland Patient Leaflet (PIL)',
  de_pdf: 'Germany SmPC + PIL (Fach-/Gebrauchsinformation)',
};

export const familyLabel = (family: string) => FAMILY_LABELS[family] ?? family;

export interface CronASplit {
  productsDiscovered: number | null;
  versionsDiscovered: number | null;
  productsIngested: number | null;
  versionsIngested: number | null;
}

const optNum = (v: unknown): number | null => (typeof v === 'number' ? v : null);

// New products vs new versions of held products for one discover run entry
// (per_route item). Same rule as the backend's cron_a_split: us_spl reports
// versions apart (outdated_discovered / updated); eu_smpc, jp_xml, au_*, ch_pdf_* and de_pdf report
// them as a subset of new_discovered / created (revisions_*). Otherwise versions
// are null (not reported), never 0.
export const cronASplit = (e: Record<string, unknown>): CronASplit => {
  const found = optNum(e['new_discovered']);
  const created = optNum(e['created']);
  if (e['outdated_discovered'] != null || e['updated'] != null) {
    return { productsDiscovered: found, versionsDiscovered: optNum(e['outdated_discovered']),
             productsIngested: created, versionsIngested: optNum(e['updated']) };
  }
  if (e['revisions_discovered'] != null) {
    const revFound = optNum(e['revisions_discovered']) ?? 0;
    const revIngested = optNum(e['revisions_ingested']) ?? 0;
    return {
      productsDiscovered: found === null ? null : Math.max(found - revFound, 0),
      versionsDiscovered: revFound,
      productsIngested: created === null ? null : Math.max(created - revIngested, 0),
      versionsIngested: revIngested,
    };
  }
  return { productsDiscovered: found, versionsDiscovered: null,
           productsIngested: created, versionsIngested: null };
};

// Readable Cron B family: route family (region first, as in Cron A) plus the doc type when the family has more than one. Unknown slugs
// fall back to the raw value.
const CRON_B_FAMILY_LABELS: Record<string, [string, boolean]> = {
  us_spl: ['US Label (SPL)', false],
  sba: ['US FDA Review', true],
  approval_letter: ['US FDA Approval Letter', false],
  fda_letter: ['US FDA Letter', true],
  ca_pm: ['Canada Product Monograph', false],
  uk_pil: ['UK Patient Leaflet', false],
  uk_spc: ['UK SmPC', false],
  eu: ['EU', true],
  epar: ['EU EPAR', true],
  jp_xml: ['Japan Label (XML)', false],
  jp_pdf: ['Japan', true],
  au: ['Australia', true],
};
const DOC_TYPE_ACRONYMS = new Set(['pi', 'cmi', 'rmp', 'smpc', 'smop', 'epar', 'pil', 'pm']);
const humanizeDocType = (dt: string) =>
  dt.split('_').map(w => (DOC_TYPE_ACRONYMS.has(w) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1))).join(' ');

// New DB-store Cron B route keys (reconciliation_runs.route, backend CRON_B_ROUTES).
export const CRON_B_ROUTE_LABELS: Record<string, string> = {
  ca_pm: 'Canada Product Monograph',
  au_pi: 'Australia · PI',
  au_cmi: 'Australia · CMI',
  eu_smpc: 'EU · SmPC',
  epar_par: 'EU EPAR · Assessment Report',
  epar_smop: 'EU EPAR · Summary of Opinion',
  sba: 'US FDA Review',
  jp_pdf_patient_leaflet: 'Japan · Patient Leaflet',
  jp_pdf_interview_form: 'Japan · Interview Form',
  jp_pdf_review_report: 'Japan · Review Report',
  jp_pdf_rmp: 'Japan · RMP',
  jp_xml: 'Japan · Package Insert (XML)',
  uk_spc: 'UK · SmPC',
  uk_pil_html: 'UK · PIL (HTML)',
  zh_pdf_package_insert: 'China · Package Insert',
  zh_pdf_review_report: 'China · Review Report',
  uk_pil: 'UK · PIL (PDF)',
};

export const cronBFamilyLabel = (slug: string): string => {
  if (CRON_B_ROUTE_LABELS[slug]) return CRON_B_ROUTE_LABELS[slug];
  const [family, docType] = slug.split('/');
  const entry = family === undefined ? undefined : CRON_B_FAMILY_LABELS[family];
  if (!entry || docType === undefined) return slug;
  const [name, showDocType] = entry;
  return showDocType ? `${name} · ${humanizeDocType(docType)}` : name;
};

const fmt = (v: number | null | undefined) => (v === null || v === undefined ? '—' : v.toLocaleString());

const formatLastRun = (iso: string | null) =>
  iso ? `${iso.slice(0, 16).replace('T', ' ')} UTC` : 'never';

export const CronASummaryTable: React.FC<{ period: CronAPeriod }> = ({ period }) => {
  const [range, setRange] = useState<number>(DEFAULT_RANGE[period]);
  const [families, setFamilies] = useState<CronAFamily[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const days = period === 'weekly' ? range * 7 : range;
    setLoading(true);
    setError(null);
    authApiClient
      .get<CronASummaryResponse>(`/admin/cron-summary?cron=A&period=${period}&days=${days}`)
      .then(res => { if (!cancelled) setFamilies(res.data.families || []); })
      .catch(e => {
        if (!cancelled) setError(e?.response?.data?.detail || 'Failed to load Cron A summary');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period, range]);

  const tips = cronAColumnTooltips('period');
  const periodLabel = (start: string) => (period === 'weekly' ? `Week of ${start}` : start);
  const numCell = (v: number | null, extra = '', title?: string) => (
    <td className={`px-4 py-3 text-right font-mono ${extra}`} title={title}>{fmt(v)}</td>
  );

  return (
    <div>
      <div className="flex items-center justify-end mb-2">
        <label className="text-xs text-slate-500 mr-2" htmlFor={`cron-a-range-${period}`}>Range</label>
        <select
          id={`cron-a-range-${period}`}
          value={range}
          onChange={e => setRange(Number(e.target.value))}
          className="text-sm border border-slate-200 rounded px-2 py-1 bg-white"
        >
          {RANGE_OPTIONS[period].map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
      <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Family</th>
              <th className="text-left px-4 py-3 font-medium">Region</th>
              <th className="text-left px-4 py-3 font-medium">{period === 'weekly' ? 'Week' : 'Day'}</th>
              <th className="text-right px-4 py-3 font-medium" title={tips.newProducts}>New products</th>
              <th className="text-right px-4 py-3 font-medium" title={tips.newVersions}>New versions</th>
              <th className="text-right px-4 py-3 font-medium" title={tips.productsIngested}>Products ingested</th>
              <th className="text-right px-4 py-3 font-medium" title={tips.versionsIngested}>Versions ingested</th>
              <th className="text-right px-4 py-3 font-medium" title={FAILED_TOOLTIP}>Hard failed</th>
              <th className="text-right px-4 py-3 font-medium" title={NOT_PARSED_TOOLTIP}>Not parsed</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>
            )}
            {!loading && error && (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-red-600">{error}</td></tr>
            )}
            {!loading && !error && families.map(fam => {
              const badge = !fam.scheduled && (
                <span
                  className="ml-2 px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-500"
                  title={`No Cron A run in the last 7 days (last run: ${formatLastRun(fam.last_run_at)})`}
                >
                  Not scheduled
                </span>
              );
              if (fam.periods.length === 0) {
                return (
                  <tr key={fam.family} data-testid={`cron-a-row-${fam.family}`} className="text-slate-400">
                    <td className="px-4 py-3 text-slate-700" title={fam.family}>{familyLabel(fam.family)}{badge}</td>
                    <td className="px-4 py-3">{fam.region ?? '—'}</td>
                    <td className="px-4 py-3">{fam.scheduled ? 'No runs in range' : '—'}</td>
                    <td className="px-4 py-3 text-right font-mono">—</td>
                    <td className="px-4 py-3 text-right font-mono">—</td>
                    <td className="px-4 py-3 text-right font-mono">—</td>
                    <td className="px-4 py-3 text-right font-mono">—</td>
                    <td className="px-4 py-3 text-right font-mono">—</td>
                    <td className="px-4 py-3 text-right font-mono">—</td>
                  </tr>
                );
              }
              return fam.periods.map((p, i) => (
                <tr
                  key={`${fam.family}|${p.period_start}`}
                  data-testid={`cron-a-row-${fam.family}`}
                >
                  <td className="px-4 py-3 text-slate-700" title={i === 0 ? fam.family : undefined}>
                    {i === 0 ? <>{familyLabel(fam.family)}{badge}</> : ''}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{i === 0 ? (fam.region ?? '—') : ''}</td>
                  <td className="px-4 py-3 text-slate-700" title={`${p.runs} run(s), source: ${p.source ?? '—'}`}>
                    {periodLabel(p.period_start)}
                  </td>
                  {numCell(p.products_discovered ?? p.discovered)}
                  {numCell(p.versions_discovered ?? null, '',
                    p.versions_discovered == null ? VERSIONS_NA_TOOLTIP : undefined)}
                  {numCell(p.products_ingested ?? p.ingested, 'text-green-700')}
                  {numCell(p.versions_ingested ?? null, 'text-green-700',
                    p.versions_ingested == null ? VERSIONS_NA_TOOLTIP : undefined)}
                  {numCell(p.failed, 'text-red-700', FAILED_TOOLTIP)}
                  {numCell(p.not_parsed, 'text-amber-700',
                    p.not_parsed === null ? NOT_PARSED_NA_TOOLTIP : NOT_PARSED_TOOLTIP)}
                </tr>
              ));
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
